import type { FoodItem as NutritionFoodItem } from "@/packages/dashboard/api/get-meals";
import { createFoodItem } from "@/packages/dashboard/api/create-food-item";
import { toOptionalNumber, validateNumberFields } from "@/packages/dashboard/utils/numeric-input-mantine-helper";

import { Alert, Button, Group, NumberInput, Paper, Stack, Text, Textarea, TextInput } from "@mantine/core";
import type { ChangeEvent, FormEvent } from "react";
import { useEffect, useRef, useState } from "react";

import type { FoodItem } from "./food-item-combobox";
import { InferNutritionButton } from "./infer-nutrition-button";

type NewFoodItemProps = {
  initialName: string;
  onCancel: () => void;
  onSuccess: (item: FoodItem) => void;
};

type SubmissionError = {
  message: string;
};

type FormState = {
  name: string;
  description: string;
  servingDescription: string;
  servingUnit: string;
  servingSize: number | undefined;
  calories: number | undefined;
  proteins: number | undefined;
  carbs: number | undefined;
  fats: number | undefined;
};

type NumericFormField = "servingSize" | "calories" | "proteins" | "carbs" | "fats";
type TextFormField = "name" | "description" | "servingDescription" | "servingUnit";

export const NewFoodItem = ({ initialName, onCancel, onSuccess }: NewFoodItemProps) => {
  const [form, setForm] = useState<FormState>({
    name: initialName,
    description: "",
    servingDescription: "",
    servingUnit: "",
    servingSize: undefined,
    calories: undefined,
    proteins: undefined,
    carbs: undefined,
    fats: undefined,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionError, setSubmissionError] = useState<SubmissionError | null>(null);
  const nameInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setForm((previous) => ({ ...previous, name: initialName }));
  }, [initialName]);

  // Whenever the user changes the original search input, and presses Create in the combobox, then we want to refocus.
  // The first time it'll happen even if we pass an empty dependency list. But this compoennt remains mounted, and so
  // we need to keep checking if the value has changed and refocus if so.
  useEffect(() => {
    if (initialName) nameInputRef.current?.focus();
  }, [initialName]);

  const resetError = () => setSubmissionError(null);

  const updateFormField = <K extends keyof FormState>(field: K, value: FormState[K]) => {
    setForm((previous) => ({ ...previous, [field]: value }));
  };

  const parseNumericValue = (value: string | number | null | undefined) => {
    if (value === null || value === undefined || value === "") {
      return undefined;
    }

    if (typeof value === "number") {
      return Number.isFinite(value) ? value : undefined;
    }

    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : undefined;
  };

  const applyInferredNutrition = (data: NutritionFoodItem) => {
    console.log(data, "data");
    setForm((previous) => ({
      ...previous,
      description: data.description?.trim() ?? "",
      servingDescription: data.serving_description?.trim() ?? "",
      servingUnit: data.serving_unit?.trim() ?? "",
      servingSize: parseNumericValue(data.serving_size),
      calories: parseNumericValue(data.calories),
      proteins: parseNumericValue(data.proteins),
      carbs: parseNumericValue(data.carbs),
      fats: parseNumericValue(data.fats),
    }));
  };

  const handleTextChange = (field: TextFormField) => (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    updateFormField(field, event.currentTarget.value);
  };

  const handleNumericChange = (field: NumericFormField) => (value: string | number) => {
    updateFormField(field, toOptionalNumber(value));
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    resetError();

    const trimmedName = form.name.trim();
    const trimmedServingUnit = form.servingUnit.trim();

    if (!trimmedName || !trimmedServingUnit) {
      setSubmissionError({ message: "Please provide a name, serving unit, and a positive serving size." });
      return;
    }

    const numericValidation = validateNumberFields<NumericFormField>([
      {
        field: "servingSize",
        value: form.servingSize,
        fieldLabel: "Serving size",
        allowZero: false,
      },
      {
        field: "calories",
        value: form.calories,
        fieldLabel: "Calories per serving",
        allowZero: true,
        integerOnly: true,
      },
      {
        field: "proteins",
        value: form.proteins,
        fieldLabel: "Protein",
        allowZero: true,
        integerOnly: false,
      },
      {
        field: "carbs",
        value: form.carbs,
        fieldLabel: "Carbohydrates",
        allowZero: true,
        integerOnly: false,
      },
      {
        field: "fats",
        value: form.fats,
        fieldLabel: "Fats",
        allowZero: true,
        integerOnly: false,
      },
    ]);

    if (!numericValidation.ok) {
      const errorMessage =
        numericValidation.field === "servingSize"
          ? "Please provide a name, serving unit, and a positive serving size."
          : numericValidation.message;
      setSubmissionError({ message: errorMessage });
      return;
    }

    const { servingSize, calories, proteins, carbs, fats } = numericValidation.values;

    setIsSubmitting(true);
    try {
      const payload = {
        food_item: {
          name: trimmedName,
          description: form.description.trim() || null,
          serving_description: form.servingDescription.trim() || null,
          serving_unit: trimmedServingUnit,
          serving_size: servingSize,
          calories,
          proteins,
          carbs,
          fats,
        },
      };

      const { id } = await createFoodItem(payload);

      const newFoodItem: FoodItem = { ...payload.food_item, id: id };

      onSuccess(newFoodItem);
    } catch (error) {
      setSubmissionError({ message: error instanceof Error ? error.message : "Unexpected error creating food item." });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Paper withBorder radius="md" p="md">
      <form onSubmit={handleSubmit}>
        <Stack gap="md">
          <div>
            <Text fw={600}>Create new food item</Text>
            <Text size="sm" c="dimmed">
              Provide full serving details to add this food to your meal.
            </Text>
          </div>

          {submissionError && (
            <Alert variant="light" color="red" onClose={resetError} withCloseButton>
              {submissionError.message}
            </Alert>
          )}

          <TextInput
            label="Name"
            placeholder="Food name"
            value={form.name}
            required
            ref={nameInputRef}
            onChange={handleTextChange("name")}
          />

          <InferNutritionButton foodName={form.name} disabled={isSubmitting} onApply={applyInferredNutrition} />

          <TextInput
            label="Serving unit"
            placeholder="e.g., cup, slice"
            value={form.servingUnit}
            required
            onChange={handleTextChange("servingUnit")}
          />

          <NumberInput
            label="Serving size in units"
            placeholder="e.g., 1.5"
            min={0.01}
            step={0.1}
            value={form.servingSize ?? ""}
            required
            onChange={handleNumericChange("servingSize")}
          />

          <NumberInput
            label="Calories per serving"
            placeholder="e.g., 250"
            min={0}
            step={1}
            value={form.calories ?? ""}
            required
            onChange={handleNumericChange("calories")}
          />

          <Group grow>
            <NumberInput
              label="Protein (g)"
              placeholder="e.g., 12"
              min={0}
              step={1}
              value={form.proteins ?? ""}
              required
              onChange={handleNumericChange("proteins")}
            />
            <NumberInput
              label="Carbohydrates (g)"
              placeholder="e.g., 45"
              min={0}
              step={1}
              value={form.carbs ?? ""}
              required
              onChange={handleNumericChange("carbs")}
            />
            <NumberInput
              label="Fats (g)"
              placeholder="e.g., 8"
              min={0}
              step={1}
              value={form.fats ?? ""}
              required
              onChange={handleNumericChange("fats")}
            />
          </Group>

          <Textarea
            label="Serving description"
            placeholder="Describe the serving (optional)"
            value={form.servingDescription}
            autosize
            minRows={2}
            onChange={handleTextChange("servingDescription")}
          />

          <Textarea
            label="Description"
            placeholder="Additional notes (optional)"
            value={form.description}
            autosize
            minRows={2}
            onChange={handleTextChange("description")}
          />

          <Group justify="flex-end">
            <Button type="button" variant="subtle" onClick={onCancel} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button type="submit" loading={isSubmitting}>
              Save
            </Button>
          </Group>
        </Stack>
      </form>
    </Paper>
  );
};
